/*
 * DD0 Gift Guard — global cart-side enforcement for auto-added free gifts.
 * Architecture: product blocks
 * register "goals" in localStorage (which gift belongs to which main product
 * and from which quantity), and this guard — loaded on every page from
 * theme.liquid — enforces them whenever the cart changes:
 *   - removes gift lines when the main product drops below the required quantity
 *     (or is removed entirely)
 *   - forces gift quantity back to 1
 *   - removes duplicated gift lines
 * All corrections are
 * batched into a single /cart/update.js call keyed by line `key`, which is
 * stable and unambiguous even with line-item properties.
 *
 * Gift lines are identified by properties._dd0_free_gift (same convention as
 * dd0-free-gifts.js and component-qs-quantity-breaks.js). Customer-purchased
 * units of the same product are never touched.
 *
 * Public API: window.DD0Gifts.registerGoal({ giftHandle, giftVariantId,
 * requiredQuantity, mainProduct }) / window.DD0Gifts.removeGoal(giftHandle)
 */
(() => {
  if (window.DD0Gifts) return;
  const STORAGE_KEY = 'dd0_gift_goals';

  const readGoals = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch (error) {
      return {};
    }
  };
  const writeGoals = (goals) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(goals));
    } catch (error) {
      /* private mode: goals only live for the page session */
    }
  };

  window.DD0Gifts = {
    registerGoal({ giftHandle, giftVariantId, requiredQuantity, mainProduct, kind }) {
      if (!giftHandle || !mainProduct) return;
      // A gift that unlocks itself would classify the main product line as
      // managed and let the guard wipe the whole cart — never register it
      if (giftHandle === mainProduct) return;
      const goals = readGoals();
      goals[giftHandle] = {
        // Keep the bookkeeping flags (auto_removed) the guard sets: re-registering
        // on every page view must not forget that the gift was auto-removed, or
        // the re-add pass would never bring it back.
        ...goals[giftHandle],
        type: 'product_quantity',
        kind: kind || 'gift',
        gift_handle: giftHandle,
        gift_variant_id: Number(giftVariantId) || null,
        required_quantity: Number(requiredQuantity) || 1,
        unlock_product_handle: mainProduct,
      };
      writeGoals(goals);
      scheduleCheck();
    },
    removeGoal(giftHandle) {
      const goals = readGoals();
      if (goals[giftHandle]) {
        delete goals[giftHandle];
        writeGoals(goals);
      }
    },
    check: () => scheduleCheck(),
  };

  let timer = null;
  let running = false;
  let rerun = false;

  function scheduleCheck() {
    clearTimeout(timer);
    timer = setTimeout(runCheck, 350);
  }

  // The main product page is authoritative for its current gift configuration.
  // Persisted rules remain useful on cart/other pages, but must not revive a
  // gift whose block or slot has since been disabled on this product page.
  function currentProductGifts() {
    const configurations = new Map();
    for (const scope of document.querySelectorAll('product-info[id^="MainProduct-"][data-url]')) {
      const path = new URL(scope.dataset.url, window.location.origin).pathname;
      const match = path.match(/\/products\/([^/]+)\/?$/);
      if (!match) continue;
      const handle = decodeURIComponent(match[1]);
      const handles = configurations.get(handle) || new Set();
      configurations.set(handle, handles);

      for (const component of scope.querySelectorAll('quantity-gifts')) {
        if (component.dataset.mainProduct !== handle || component.offsetParent === null) continue;
        for (const gift of component.querySelectorAll('.quantity-gift[data-handle][data-product]')) {
          if (gift.dataset.kind !== 'benefit') handles.add(gift.dataset.handle);
        }
      }
      // Gifts configured inside the quantity selector are independent of the
      // Gifts on quantity block and must keep their own restoration behavior.
      for (const component of scope.querySelectorAll('qs-breaks')) {
        if (component.dataset.productHandle !== handle) continue;
        for (const gift of component.querySelectorAll('.qs-break__upsell[data-type="gift"][data-handle][data-product]')) {
          handles.add(gift.dataset.handle);
        }
      }
    }
    return configurations;
  }

  async function runCheck() {
    if (running) {
      rerun = true;
      return;
    }
    running = true;
    try {
      const goals = readGoals();

      // Sanitize self-referential goals (gift_handle == its own unlock
      // product): via the legacy fallback in classify() they mark the main
      // product line itself as managed, its units count as 0 and the guard
      // empties the entire cart ~1s after every add. Dropping them here also
      // heals carts of customers who already have one stored.
      let goalsDirty = false;
      Object.keys(goals).forEach((key) => {
        if (goals[key]?.gift_handle === goals[key]?.unlock_product_handle) {
          delete goals[key];
          goalsDirty = true;
        }
      });

      const cart = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' }).then((response) =>
        response.json()
      );

      const configurations = currentProductGifts();
      const inactiveGift = (parent, handle) =>
        configurations.has(parent) && !configurations.get(parent).has(handle);
      for (const [handle, goal] of Object.entries(goals)) {
        if (goal.kind !== 'upsell' && inactiveGift(goal.unlock_product_handle, handle)) {
          delete goals[handle];
          goalsDirty = true;
        }
      }

      // A line is managed when it self-describes via properties (new lines) or
      // when a goal is registered for its handle (legacy lines
      // added before the _dd0_* properties existed)
      const classify = (item) => {
        const goal = goals[item.handle];
        if (item.properties?._dd0_free_gift || item.properties?._dd0_upsell) {
          return {
            kind: item.properties._dd0_free_gift ? 'gift' : 'upsell',
            parent: item.properties._dd0_gift_parent || goal?.unlock_product_handle || null,
            min: parseInt(item.properties._dd0_gift_min, 10) || goal?.required_quantity || 1,
          };
        }
        // Legacy fallback (lines without _dd0_* properties): never against the
        // unlock product itself — a paid main line must not become removable
        if (goal && goal.unlock_product_handle !== item.handle) {
          return {
            kind: goal.kind || 'gift',
            parent: goal.unlock_product_handle,
            min: goal.required_quantity || 1,
          };
        }
        return null;
      };

      const managed = cart.items
        .map((item) => ({ item, info: classify(item) }))
        .filter((entry) => entry.info);

      // Runtime style layer: hides the cart-line
      // controls regardless of properties/Liquid, survives section re-renders
      updateGiftStyles(managed);

      // Units of a given main product: real lines only, never gifts/upsells
      const managedKeys = new Set(managed.map((entry) => entry.item.key));
      const unitsOf = (handle) =>
        cart.items
          .filter((item) => item.handle === handle && !managedKeys.has(item.key))
          .reduce((sum, item) => sum + item.quantity, 0);

      // Re-add pass: lines the guard itself removed on a tier drop come back
      // automatically when the tier is reached again (e.g. cart stepper
      // 2 -> 1 -> 2). Manual removals never set auto_removed, so an upsell the
      // user deleted with its remove button stays removed.
      const handlesInCart = new Set(cart.items.map((item) => item.handle));

      // A flag only means something while its line is gone: once the line is
      // back in the cart (re-add, PDP add, manual add) clear it so a stale
      // flag can't resurrect a line the customer later deletes on purpose
      Object.values(goals).forEach((goal) => {
        if (goal.auto_removed && handlesInCart.has(goal.gift_handle)) {
          delete goal.auto_removed;
          goalsDirty = true;
        }
      });
      if (goalsDirty) {
        writeGoals(goals);
        goalsDirty = false;
      }

      const readds = Object.values(goals).filter(
        (goal) =>
          goal.auto_removed &&
          goal.gift_variant_id &&
          !handlesInCart.has(goal.gift_handle) &&
          unitsOf(goal.unlock_product_handle) >= (goal.required_quantity || 1)
      );
      if (readds.length) {
        await readdLines(readds, goals, managed);
        rerun = true; // fresh pass over the new cart state (clamps/dupes)
        return;
      }

      if (!managed.length) return;

      const updates = {};
      const seenHandles = new Set();
      managed.forEach(({ item, info }) => {
        if (!info.parent) return;
        if (info.kind === 'gift' && inactiveGift(info.parent, item.handle)) {
          updates[item.key] = 0;
          return; // disabled promotion: never persist an auto_removed restore rule
        }
        if (unitsOf(info.parent) < info.min) {
          updates[item.key] = 0; // tier no longer reached -> drop gift AND upsell
          // Remember it was the guard who removed it (and everything needed to
          // rebuild the line), so the re-add pass can restore it later
          goals[item.handle] = {
            ...(goals[item.handle] || {}),
            type: 'product_quantity',
            kind: info.kind,
            gift_handle: item.handle,
            gift_variant_id: item.variant_id,
            required_quantity: info.min,
            unlock_product_handle: info.parent,
            auto_removed: true,
          };
          goalsDirty = true;
        } else if (seenHandles.has(item.handle)) {
          updates[item.key] = 0; // duplicated line -> keep only the first
        } else if (item.quantity > 1) {
          updates[item.key] = 1; // bundle extras are always quantity 1
        }
        seenHandles.add(item.handle);
      });
      if (goalsDirty) writeGoals(goals);

      if (!Object.keys(updates).length) return;

      // Wiping every remaining line is either a legitimate orphan cleanup
      // (main product deleted, gifts left behind) or a mid-flight race: the
      // qs/v1/free-gifts flows add the extras BEFORE the main product, so a
      // pass landing in that window sees a gifts-only cart. Re-read once and
      // only proceed if the cart is still in the same state.
      const removalCount = Object.values(updates).filter((quantity) => quantity === 0).length;
      if (removalCount === cart.items.length) {
        await new Promise((resolve) => setTimeout(resolve, 800));
        const recheck = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' }).then((res) =>
          res.json()
        );
        const sameLines =
          recheck.items.length === cart.items.length &&
          recheck.items.every((item) => cart.items.some((prev) => prev.key === item.key));
        if (!sameLines) {
          rerun = true; // cart changed mid-flight (e.g. main add landed): recompute
          return;
        }
      }

      // Render through the cart items component's own section map
      // (cart-drawer-items targets .drawer__inner, cart-items the cart page)
      const cartItems =
        document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
      const body = { updates };
      if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
        body.sections = cartItems.getSectionsToRender().map((section) => section.section);
        body.sections_url = window.location.pathname;
      }
      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify(body),
      }).then((res) => res.json());

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dd0-gift-guard', cartData: response });
      }
      if (cartItems && response.sections) renderCartSections(cartItems, response);
    } catch (error) {
      console.warn('[dd0-gift-guard]', error);
    } finally {
      running = false;
      if (rerun) {
        rerun = false;
        scheduleCheck();
      }
    }
  }

  // Rebuilds the gift/upsell lines from their goals (same properties contract
  // as component-qs-quantity-breaks.js giftLineItem/upsellLineItem) and adds
  // them in one /cart/add.js call, rendering through the cart component's
  // section map like the updates path above. Flags are cleared even on a
  // failed add (e.g. sold out) so the rerun pass can't loop on it forever.
  async function readdLines(readds, goals, managed) {
    const items = readds.map((goal) => ({
      id: goal.gift_variant_id,
      quantity: 1,
      properties: {
        [goal.kind === 'upsell' ? '_dd0_upsell' : '_dd0_free_gift']: true,
        _dd0_gift_parent: goal.unlock_product_handle,
        _dd0_gift_min: goal.required_quantity || 1,
      },
    }));

    readds.forEach((goal) => delete goals[goal.gift_handle].auto_removed);
    writeGoals(goals);

    const cartItems =
      document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
    const body = { items };
    if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
      body.sections = cartItems.getSectionsToRender().map((section) => section.section);
      body.sections_url = window.location.pathname;
    }
    const response = await fetch(routes.cart_add_url, {
      ...fetchConfig(),
      body: JSON.stringify(body),
    }).then((res) => res.json());

    if (response.status) {
      console.warn('[dd0-gift-guard] re-add failed:', response.description || response.message);
      return;
    }

    // The new lines are managed: refresh the style layer before the rerun pass
    updateGiftStyles(
      managed.concat(
        readds.map((goal) => ({
          item: { handle: goal.gift_handle },
          info: { kind: goal.kind || 'gift' },
        }))
      )
    );
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dd0-gift-guard', cartData: response });
    }
    if (cartItems && response.sections) renderCartSections(cartItems, response);
  }

  // Style layer (#dd0-gifts-style): one rule set
  // per managed handle, hooked on the .dd0-cart-item--{handle} line classes
  // present in both the drawer and the cart page. Gifts lose quantity stepper
  // AND remove button; checkbox upsells only the stepper. Rebuilt on every
  // guard pass and removed when nothing is managed, so a later standalone
  // purchase of the same product is not affected.
  function updateGiftStyles(managed) {
    const existing = document.getElementById('dd0-gifts-style');
    if (!managed.length) {
      existing?.remove();
      return;
    }
    const rules = [];
    const seen = new Set();
    managed.forEach(({ item, info }) => {
      if (seen.has(item.handle)) return;
      seen.add(item.handle);
      const base = `.cart-item.dd0-cart-item--${item.handle}`;
      // .dd0-cart-items variants: out-rank the drawer's own
      // `display: inline-flex !important` quantity rules
      rules.push(
        `${base} .cart-item__quantity-wrapper, ${base} quantity-input, ` +
          `.dd0-cart-items ${base} .cart-item__quantity-wrapper, ` +
          `.dd0-cart-items ${base} quantity-input.quantity { display: none !important; }`
      );
      if (info.kind === 'gift') {
        rules.push(`${base} cart-remove-button { display: none !important; }`);
      }
    });
    const style = existing || document.createElement('style');
    style.id = 'dd0-gifts-style';
    style.textContent = rules.join('\n');
    if (!existing) document.head.appendChild(style);
  }

  // Same render flow as CartItems.updateQuantity (cart.js): replace each
  // section's inner contents and toggle the is-empty classes — without them
  // the open drawer shows a blank panel when the cart empties. Avoids
  // cart-drawer.renderContents(), which force-opens the drawer.
  function renderCartSections(cartItems, parsedState) {
    const isEmpty = parsedState.item_count === 0;
    cartItems.classList.toggle('is-empty', isEmpty);
    document.querySelector('cart-drawer')?.classList.toggle('is-empty', isEmpty);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', isEmpty);

    cartItems.getSectionsToRender().forEach((section) => {
      const html = parsedState.sections[section.section];
      const container = document.getElementById(section.id);
      if (!html || !container) return;
      const target = container.querySelector(section.selector) || container;
      const replacement = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(section.selector);
      if (replacement) target.innerHTML = replacement.innerHTML;
    });
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source !== 'dd0-gift-guard') scheduleCheck();
    });
  }
  // Page-load check only when there is something to guard — avoids a /cart.js
  // request on every page view. Cart-change events always check (cheap, and
  // the user is actively interacting with the cart).
  const initialCheck = () => {
    if (Object.keys(readGoals()).length) scheduleCheck();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialCheck);
  } else {
    initialCheck();
  }
})();
